import React, { useState } from "react";
import { Phone, Mail, Instagram, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import ServiceMap from "@/components/site/ServiceMap";
import EmbedHtml from "@/components/site/EmbedHtml";
import { useSiteContent } from "@/lib/siteContent";
import { useSeo } from "@/lib/useSeo";
import { PageHeader } from "./AboutPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { content, loading } = useSiteContent();
  useSeo("Contact", "Contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch soon. ✨");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (loading || !content) return null;
  const site = content.site || {};
  const has = (v) => v && String(v).trim().length > 0;
  const customEmbed = (site.contactFormEmbed || "").trim();
  const rows = [
    { icon: Phone, label: "Phone", value: site.contactPhone },
    { icon: Mail, label: "Email", value: site.contactEmail },
    { icon: Instagram, label: "Instagram", value: site.instagramHandle },
    { icon: MapPin, label: "Service Area", value: site.serviceArea },
  ].filter((r) => has(r.value));

  return (
    <div data-testid="contact-page">
      <PageHeader title="Contact" script="Say Hello" subtitle="We'd love to hear from you." />
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-4xl">Let's make it <span className="font-script text-cozy-gold">magical</span></h2>
                <p className="text-cozy-brown-soft mt-3">Drop us a note or reach out anytime — we typically respond within 24 hours.</p>
              </div>
              {rows.length > 0 && (
                <div className="space-y-4 bg-white rounded-3xl p-7 shadow-cozy border border-cozy-gold/10">
                  {rows.map((r) => (
                    <ContactRow key={r.label} icon={r.icon} label={r.label} value={r.value} />
                  ))}
                </div>
              )}
              {site.showServiceMap !== false && (
                <div data-testid="contact-service-map">
                  <ServiceMap customUrl={site.serviceMapImageUrl} />
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={120}>
            {customEmbed ? (
              <EmbedHtml
                html={customEmbed}
                className="bg-white rounded-3xl p-6 shadow-cozy-lg border border-cozy-gold/15"
                data-testid="contact-embed"
              />
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl p-8 shadow-cozy-lg border border-cozy-gold/15 space-y-5" data-testid="contact-form">
                <Input label="Name" testid="contact-name" required value={form.name} onChange={(v) => update("name", v)} />
                <Input label="Email" testid="contact-email" type="email" required value={form.email} onChange={(v) => update("email", v)} />
                <Input label="Phone" testid="contact-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Message</span>
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition" />
                </label>
                <button data-testid="contact-submit" disabled={submitting} className="w-full px-7 py-3.5 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition shadow-cozy disabled:opacity-60">
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-cozy-pink/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-cozy-gold" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-cozy-brown-soft">{label}</div>
        <div className="font-serif text-lg text-cozy-brown">{value}</div>
      </div>
    </div>
  );
}

function Input({ label, testid, type = "text", required, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{label}</span>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition" />
    </label>
  );
}
